import type { ApolloQueryResult, FetchPolicy } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useCallback } from "react";

import { GET_TOE_LINES } from "../queries";
import type { IToeLinesConnection } from "../types";
import type { GetToeLinesQueryVariables } from "gql/graphql";
import { useDebouncedCallback } from "hooks";

const DEBOUNCE_DELAY = 500;
const PROCESSING_ROWS_LIMIT = 5000;
interface IUseToeLinesPaginationOptions {
  variables: GetToeLinesQueryVariables;
  fetchPolicy?: FetchPolicy;
  nextFetchPolicy?: FetchPolicy;
}

interface IToeLinesQueryResult {
  group: {
    __typename: "Group";
    name: string;
    toeLinesConnection: IToeLinesConnection;
  };
}

export interface IUseToeLinesPaginationReturn {
  data?: IToeLinesQueryResult;
  fetchReportData: () => Promise<void>;
  handleSearch: (search: string) => void;
  handleNextPage: () => Promise<void>;
  refetch: () => Promise<ApolloQueryResult<IToeLinesQueryResult>>;
}

export function useToeLinesPagination({
  variables,
  fetchPolicy = "network-only",
  nextFetchPolicy = "cache-first",
}: IUseToeLinesPaginationOptions): IUseToeLinesPaginationReturn {
  const { data, fetchMore, refetch } = useQuery<IToeLinesQueryResult>(
    GET_TOE_LINES,
    {
      fetchPolicy,
      nextFetchPolicy,
      variables,
    },
  );

  const handleNextPage = useCallback(async (): Promise<void> => {
    const pageInfo =
      data === undefined
        ? { endCursor: [], hasNextPage: false }
        : {
            endCursor: JSON.parse(
              data.group.toeLinesConnection.pageInfo.endCursor,
            ) as string[],
            hasNextPage: data.group.toeLinesConnection.pageInfo.hasNextPage,
          };

    if (pageInfo.hasNextPage) {
      await fetchMore({ variables: { after: pageInfo.endCursor.map(String) } });
    }
  }, [data, fetchMore]);

  const fetchReportData = useCallback(async (): Promise<void> => {
    const fetchMoreData = async (
      endCursor: string[],
      count: number,
    ): Promise<void> => {
      const result = await fetchMore({
        variables: { after: endCursor.map(String) },
      });
      const newEndCursor = JSON.parse(
        result.data.group.toeLinesConnection.pageInfo.endCursor,
      );
      const { hasNextPage } = result.data.group.toeLinesConnection.pageInfo;

      if (Boolean(hasNextPage) && count < PROCESSING_ROWS_LIMIT) {
        await fetchMoreData(
          newEndCursor,
          count + result.data.group.toeLinesConnection.edges.length,
        );
      }
    };

    const pageInfo =
      data === undefined
        ? { count: 0, endCursor: [], hasNextPage: false }
        : {
            count: data.group.toeLinesConnection.edges.length,
            endCursor: JSON.parse(
              data.group.toeLinesConnection.pageInfo.endCursor,
            ) as string[],
            hasNextPage: data.group.toeLinesConnection.pageInfo.hasNextPage,
          };

    if (pageInfo.hasNextPage) {
      await fetchMoreData(pageInfo.endCursor, pageInfo.count);
    }
  }, [data, fetchMore]);

  const handleSearch = useDebouncedCallback((search: string): void => {
    void refetch({ filters: { rootNickname: search } });
  }, DEBOUNCE_DELAY);

  return {
    data,
    fetchReportData,
    handleNextPage,
    handleSearch,
    refetch,
  };
}
